// frontend/src/api/PlanClient.ts
import { apiFetch } from "./baseClient";
import { PlanResponse } from "../model/backendTypes";

export interface PlanPollingOptions {
  userId: number;
  projectId: number;
  intervalMs?: number;
  onFreshPlan: (
    planCsv: string | null,
    fulfillmentsJson: string | null,
    syntaxchecksJson: string | null,
    meta: {
      quality: number | null;
      improvement: number | null;
      scheduler_assessment: string | null;
      created_at: string | null;
      rules_at: string | null;
      solve_at: string | null;
      solve_estimate_at: string | null;
      solve_locked_at: string | null;
    }
  ) => void;
}

interface PlanHeartbeatResponse {
  project_id: number;
  has_fresh: boolean;
}

/**
 * Startet ein zweistufiges Polling:
 * 1. /heartbeat (leichtgewichtig)
 * 2. nur bei has_fresh=true -> /plan (voller Payload)
 */
export function startPlanPolling(opts: PlanPollingOptions): () => void {
  const { userId, projectId, onFreshPlan, intervalMs = 5000 } = opts;

  let timer: number | undefined;
  let stopped = false;

  const stop = () => {
    stopped = true;

    if (timer !== undefined) {
      window.clearInterval(timer);
      timer = undefined;
    }
  };

  const stopOnInvalidProject = async (
    resp: Response,
    label: "heartbeat" | "plan fetch"
  ): Promise<boolean> => {
    if (resp.status !== 403 && resp.status !== 404) {
      return false;
    }

    const text = await resp.text();
    console.warn(`plan ${label} stopped:`, resp.status, text, {
      userId,
      projectId,
    });

    stop();
    return true;
  };

  const tick = async () => {
    if (stopped) {
      return;
    }

    try {
      // --- STEP 1: Heartbeat ---
      const heartbeatPath = `/api/projects/plan/heartbeat?user_id=${encodeURIComponent(
        String(userId)
      )}&project_id=${encodeURIComponent(String(projectId))}`;

      const heartbeatResp = await apiFetch(heartbeatPath, {
        method: "GET",
        headers: { "cache-control": "no-store" },
      });

      if (stopped) {
        return;
      }

      if (!heartbeatResp.ok) {
        if (await stopOnInvalidProject(heartbeatResp, "heartbeat")) {
          return;
        }

        const text = await heartbeatResp.text();
        console.warn("plan heartbeat failed:", heartbeatResp.status, text);
        return;
      }

      const heartbeat: PlanHeartbeatResponse = await heartbeatResp.json();

      if (stopped || !heartbeat.has_fresh) {
        return;
      }

      // --- STEP 2: Full Plan Fetch ---
      const path = `/api/projects/plan?user_id=${encodeURIComponent(
        String(userId)
      )}&project_id=${encodeURIComponent(String(projectId))}`;

      const resp = await apiFetch(path, {
        method: "GET",
        headers: { "cache-control": "no-store" },
      });

      if (stopped) {
        return;
      }

      if (!resp.ok) {
        if (await stopOnInvalidProject(resp, "plan fetch")) {
          return;
        }

        const text = await resp.text();
        console.warn("plan fetch failed:", resp.status, text);
        return;
      }

      const data: PlanResponse = await resp.json();

      if (stopped) {
        return;
      }

      if (data.is_fresh) {
        onFreshPlan(
          data.plan_csv,
          data.fulfillments_json,
          data.syntaxchecks_json,
          {
            quality: data.quality,
            improvement: data.improvement,
            scheduler_assessment: data.scheduler_assessment,
            created_at: data.created_at,
            rules_at: data.rules_at,
            solve_at: data.solve_at,
            solve_estimate_at: data.solve_estimate_at,
            solve_locked_at: data.solve_locked_at,
          }
        );
      }
    } catch (e) {
      if (!stopped) {
        console.warn("plan poll error:", e);
      }
    }
  };

  timer = window.setInterval(tick, Math.max(200, intervalMs));
  void tick();

  return stop;
}

/**
 * Importiert Plan, bereinigt ihn und berechnet Fulfillments
 */
export async function importPlan(
  userId: number,
  projectId: number,
  planCsv: string
): Promise<PlanResponse> {
  const resp = await apiFetch("/api/projects/plan/import", {
    method: "POST",
    body: {
      user_id: userId,
      project_id: projectId,
      plan_csv: planCsv,
    } as any,
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as PlanResponse;
}
